import React, { useState, KeyboardEvent } from 'react'
import TagChip from '../TagChip/TagChip'
import styles from './TagInput.module.scss'

export interface TagInputProps {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  suggestions?: string[]
  suggestionsLimit?: number
}

const normalize = (raw: string): string => raw.trim().toLowerCase()

export default function TagInput({
  value,
  onChange,
  placeholder = 'Ajouter un tag…',
  disabled,
  autoFocus,
  suggestions,
  suggestionsLimit = 12,
}: TagInputProps) {
  const [draft, setDraft] = useState('')

  const commit = (raw: string) => {
    const t = normalize(raw)
    if (!t) return
    if (value.includes(t)) {
      setDraft('')
      return
    }
    onChange([...value, t])
    setDraft('')
  }

  const remove = (tag: string) => {
    onChange(value.filter((t) => t !== tag))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit(draft)
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault()
      remove(value[value.length - 1])
    }
  }

  const handleBlur = () => {
    if (draft.trim() !== '') commit(draft)
  }

  const draftNorm = normalize(draft)
  const filteredSuggestions = (suggestions ?? [])
    .filter((s) => !value.includes(s))
    .filter((s) => draftNorm === '' || s.toLowerCase().includes(draftNorm))
    .slice(0, suggestionsLimit)

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        {value.map((tag) => (
          <TagChip key={tag} label={tag} onRemove={disabled ? undefined : () => remove(tag)} />
        ))}
        <input
          type="text"
          className={styles.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      </div>
      {filteredSuggestions.length > 0 && (
        <div className={styles.suggestions} role="listbox" aria-label="Tags suggérés">
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              className={styles.suggestionChip}
              onMouseDown={(e) => {
                e.preventDefault()
                commit(s)
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
